// TransducerSelection.js
import React, { useState, useEffect, useRef } from 'react';
import '../FishFinderBuilder.css';
import { transducersData } from '../data/transducersData';
import ImageModal from './ImageModal';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { glossary } from '../data/glossary';
import FindBestPriceButton from './PriceComparison/FindBestPriceButton';
import { fetchLowestPrice } from '../utils/api';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 80px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;

  @media (min-width: 769px) {
    height: 100px;
  }
`;

const ProductImage = styled.img`
  width: 85%;
  height: 85%;
  object-fit: contain;

  @media (min-width: 769px) {
    width: 95%;
    height: 95%;
  }
`;

const Tooltip = ({ text }) => {
  const [visible, setVisible] = useState(false);
  const iconRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (visible && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 6, // 6px gap
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }
  }, [visible]);

  return (
    <>
      <span
        ref={iconRef}
        style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        tabIndex={0}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
      >
        <span style={{ color: '#4CAF50', marginLeft: 4, fontWeight: 'bold' }}>ⓘ</span>
      </span>
      {visible && createPortal(
        <span style={{
          minWidth: '180px',
          maxWidth: '260px',
          backgroundColor: '#222',
          color: '#fff',
          textAlign: 'left',
          borderRadius: '6px',
          padding: '8px',
          position: 'absolute',
          zIndex: 2000,
          top: coords.top,
          left: coords.left,
          transform: 'translateX(-50%)',
          opacity: 1,
          fontSize: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          whiteSpace: 'normal',
          pointerEvents: 'none',
        }}>
          {text}
        </span>,
        document.body
      )}
    </>
  );
};

const TransducerSelection = ({
  selectedLivescope = null,
  setSelectedLivescope = () => {},
  selectedOpenWater = null,
  setSelectedOpenWater = () => {},
  selectedIceFisher = null,
  setSelectedIceFisher = () => {},
  transducerType = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [modalImage, setModalImage] = useState({ isOpen: false, url: '', alt: '' });
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [filters, setFilters] = useState({
    maxRange: [],
    blackBoxRequired: null,
    imagingModes: []
  });
  const [filteredTransducers, setFilteredTransducers] = useState([]);
  
  // Add state for tracking lowest prices
  const [lowestPrices, setLowestPrices] = useState({});

  // Function to fetch lowest price for a transducer
  const fetchTransducerLowestPrice = async (transducer) => {
    if (!transducer?.csvLookupId) {
      console.log(`⚠️ [TransducerSelection] No csvLookupId for ${transducer?.name || 'unknown transducer'}`);
      return null;
    }
    
    try {
      console.log(`🔍 [TransducerSelection] Calling fetchLowestPrice for ${transducer.name} with csvLookupId: "${transducer.csvLookupId}", MSRP: $${transducer.msrp}`);
      
      // Pass the transducer's MSRP to calculate accurate savings
      const lowestPriceData = await fetchLowestPrice(transducer.csvLookupId, true, transducer.msrp);
      
      if (lowestPriceData) {
        console.log(`✅ [TransducerSelection] Received pricing data for ${transducer.name}:`, lowestPriceData);
        setLowestPrices(prev => ({
          ...prev,
          [transducer.id]: lowestPriceData
        }));
        return lowestPriceData;
      } else {
        console.log(`❌ [TransducerSelection] No pricing data returned for ${transducer.name}`);
      }
    } catch (error) {
      console.error(`❌ [TransducerSelection] Error fetching price for ${transducer.name}:`, error);
    }
    return null;
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Debug transducersData
    console.log('TransducerSelection Mounted');
    console.log('TransducersData array length:', transducersData.length);
    console.log('TransducersData with livescope type:', transducersData.filter(t => t.type === 'livescope').length);
    console.log('Current transducer type passed to component:', transducerType);
    
    // Check if we need to map 'liveImaging' to 'livescope'
    if (transducerType === 'liveImaging') {
      console.log('Mapping liveImaging to livescope type - options count:', 
        transducersData.filter(t => t.type === 'livescope').length);
    }
  }, [transducerType]);

  // Effect to fetch lowest prices for transducers with csvLookupId
  useEffect(() => {
    const transducersWithPricing = transducersData.filter(transducer => 
      transducer.csvLookupId && !lowestPrices[transducer.id]
    );
    
    if (transducersWithPricing.length > 0) {
      console.log(`🔍 [TransducerSelection] Fetching prices for ${transducersWithPricing.length} transducers with pricing data:`,
        transducersWithPricing.map(t => ({ id: t.id, name: t.name, csvLookupId: t.csvLookupId }))
      );
      transducersWithPricing.forEach(transducer => {
        console.log(`📡 [TransducerSelection] Fetching price for ${transducer.name} (${transducer.csvLookupId})`);
        fetchTransducerLowestPrice(transducer);
      });
    } else {
      console.log(`✅ [TransducerSelection] All transducers with pricing data already fetched or no transducers with csvLookupId found`);
    }
  }, [lowestPrices]);

  // Get filter options from transducersData
  const rangeOptions = [...new Set(transducersData
    .filter(t => t.type === 'livescope')
    .map(t => t.maxRange?.split(' ')[0] === "100" ? "100-200 ft" : 
              t.maxRange?.split(' ')[0] === "200" ? "100-200 ft" :
              t.maxRange?.split(' ')[0] === "300" ? "300+ ft" :
              t.maxRange?.split(' ')[0] === "500" ? "300+ ft" : "100-200 ft"))]
    .sort((a, b) => parseInt(a) - parseInt(b));

  // Get unique imaging modes
  const availableImagingModes = ["Forward", "Down", "Perspective"];

  // Filter transducers by type
  const getCurrentOptions = () => {
    console.log('getCurrentOptions called with transducerType:', transducerType);
    
    // Use default fallback to 'livescope' if transducerType is null or undefined
    const effectiveType = transducerType || 'liveImaging';
    
    switch (effectiveType) {
      case 'liveImaging':
        const livescopeOptions = transducersData.filter(t => t.type === 'livescope');
        console.log('Livescope options count:', livescopeOptions.length);
        return livescopeOptions;
      case 'openWater':
        return transducersData.filter(t => t.type === 'openWater');
      case 'iceFishing':
        return transducersData.filter(t => t.type === 'iceFisher');
      default:
        console.warn('Unknown transducer type:', effectiveType, 'falling back to livescope');
        return transducersData.filter(t => t.type === 'livescope');
    }
  };

  // Immediately populate filtered transducers on component mount
  useEffect(() => {
    const initialOptions = getCurrentOptions();
    console.log('Initial transducer options:', initialOptions);
    console.log('Initial transducerType:', transducerType);
    setFilteredTransducers(initialOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getCurrentOptions is derived from transducerType + data
  }, [transducerType]);

  // Update filtered transducers when filters or type changes
  useEffect(() => {
    const currentOptions = getCurrentOptions();
    console.log('TransducerSelection: transducerType =', transducerType);
    console.log('TransducerSelection: currentOptions =', currentOptions);
    
    if (transducerType === 'liveImaging') {
      // Apply filters for live imaging transducers
      let filtered = currentOptions.filter(transducer => {
        // Filter by max range
        if (filters.maxRange.length > 0) {
          const transducerRange = parseInt(transducer.maxRange?.replace(/\D/g, '')) || 0;
          const matchesRange = filters.maxRange.some(range => {
            // Check if range is in the selected range bracket
            if (range === "100-200 ft") {
              return transducerRange >= 100 && transducerRange <= 200;
            } else if (range === "300+ ft") {
              return transducerRange >= 300;
            }
            return false;
          });
          if (!matchesRange) return false;
        }
        
        // Filter by black box requirement
        if (filters.blackBoxRequired !== null) {
          if (filters.blackBoxRequired !== transducer.blackBoxRequired) {
            return false;
          }
        }
        
        // Filter by imaging modes - using AND logic
        if (filters.imagingModes.length > 0) {
          // Check if the transducer supports ALL selected imaging modes
          const hasAllModes = filters.imagingModes.every(mode => {
            return transducer.imagingModes?.includes(mode);
          });
          if (!hasAllModes) return false;
        }
        
        return true;
      });
      
      console.log('TransducerSelection: filtered transducers =', filtered);
      setFilteredTransducers(filtered);
    } else {
      // For other transducer types, no filtering
      setFilteredTransducers(currentOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getCurrentOptions matches transducerType + filters
  }, [transducerType, filters]);

  const handleSelect = (transducer) => {
    // Set the selection first
    let updatedState = { ...location.state || {} };
    
    switch (transducerType) {
      case 'liveImaging':
        setSelectedLivescope(transducer);
        updatedState.selectedLivescope = transducer;
        break;
      case 'openWater':
        setSelectedOpenWater(transducer);
        updatedState.selectedOpenWater = transducer;
        break;
      case 'iceFishing':
        setSelectedIceFisher(transducer);
        updatedState.selectedIceFisher = transducer;
        break;
      default:
        break;
    }
    
    // Add a small delay to ensure state updates before navigation
    setTimeout(() => {
      // Make sure the transducer powerDraw12V property is included
      const transducerWithPowerDraw = {
        ...transducer,
        powerDraw12V: transducer.powerDraw12V || (transducerType === 'liveImaging' ? '1.7' : '0.3')
      };
      
      // Include both the named transducer field (selectedLivescope, etc.) AND the generic transducer field
      navigate('/custom-builder', {
        state: {
          ...updatedState,
          transducer: transducerWithPowerDraw
        }
      });
    }, 100);
  };

  const getTitle = () => {
    switch (transducerType) {
      case 'liveImaging':
        return 'Live Imaging Transducer';
      case 'openWater':
        return 'Open Water Transducer';
      case 'iceFishing':
        return 'Ice Fishing Transducer';
      default:
        return 'Select Transducer';
    }
  };

  // Function to toggle card expansion
  const toggleCardExpansion = (transducerId, event) => {
    event.stopPropagation();
    
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(transducerId)) {
        newSet.delete(transducerId);
      } else {
        newSet.add(transducerId);
      }
      return newSet;
    });
  };

  // Handle range filter toggle
  const handleRangeToggle = (range) => {
    setFilters(prev => ({
      ...prev,
      maxRange: prev.maxRange.includes(range)
        ? prev.maxRange.filter(r => r !== range)
        : [...prev.maxRange, range]
    }));
  };

  // Handle imaging modes filter
  const handleImagingModesChange = (mode) => {
    setFilters(prev => ({
      ...prev,
      imagingModes: prev.imagingModes.includes(mode)
        ? prev.imagingModes.filter(m => m !== mode)
        : [...prev.imagingModes, mode]
    }));
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.maxRange.length > 0 || 
           filters.blackBoxRequired !== null || 
           filters.imagingModes.length > 0;
  };

  // Reset all filters
  const resetAllFilters = () => {
    setFilters({
      maxRange: [],
      blackBoxRequired: null,
      imagingModes: []
    });
  };

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: '100%',
      margin: 0,
      padding: 0,
      backgroundColor: '#000000',
      minHeight: '100vh'
    }}>
      {/* Back Button */}
      <div style={{
        backgroundColor: '#000000',
        padding: '10px 0 0 0',
        paddingLeft: '24px'
      }}>
        <button
          onClick={() => navigate('/custom-builder')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '0 0 10px 0',
            width: 'fit-content'
          }}
        >
          ← Back to Builder
        </button>
      </div>

      {/* Main Content with Sidebar for filters (only for live imaging) */}
      <div style={{
        display: 'flex',
        margin: 0,
        padding: 0,
        width: '100%',
        backgroundColor: '#000000'
      }}>
        {/* Filters Sidebar - Only show for Live Imaging transducers */}
        {transducerType === 'liveImaging' && (
          <div style={{
            width: '25%',
            backgroundColor: '#1a1a1a',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.2)',
            position: 'sticky',
            top: '75px',
            height: 'calc(100vh - 75px)',
            display: 'flex',
            flexDirection: 'column',
            padding: '10px 15px',
            margin: 0,
            zIndex: 10,
            alignSelf: 'flex-start'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <h3 style={{ 
                fontSize: '14px',
                margin: '0',
                color: '#ffffff',
                fontWeight: '600'
              }}>Filter Options</h3>
              {hasActiveFilters() && (
                <button
                  onClick={resetAllFilters}
                  style={{
                    fontSize: '11px',
                    padding: '4px 8px',
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: '#ffffff',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    opacity: 0.8,
                    transition: 'opacity 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.8'}
                >
                  Reset
                </button>
              )}
            </div>
            
            <div style={{
              overflowY: 'auto',
              padding: '0 0 10px 0',
              flex: 1
            }}>
              <div className="filter-section" style={{ 
                marginBottom: '8px',
                paddingBottom: '8px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h4 style={{ 
                  fontSize: '12px',
                  marginBottom: '4px',
                  color: '#b8c7d9',
                  fontWeight: '500'
                }}>Max Range</h4>
                {rangeOptions.map(range => (
                  <label key={range} className="filter-checkbox" style={{
                    fontSize: '12px',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e0e0e0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={filters.maxRange.includes(range)}
                      onChange={() => handleRangeToggle(range)}
                      style={{
                        width: '12px',
                        height: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    {range}
                  </label>
                ))}
              </div>

              <div className="filter-section" style={{ 
                marginBottom: '8px',
                paddingBottom: '8px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h4 style={{ 
                  fontSize: '12px',
                  marginBottom: '4px',
                  color: '#b8c7d9',
                  fontWeight: '500'
                }}>Black Box Required</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label className="filter-checkbox" style={{
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e0e0e0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      checked={filters.blackBoxRequired === true}
                      onChange={() => setFilters(prev => ({ ...prev, blackBoxRequired: true }))}
                      style={{
                        width: '12px',
                        height: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    Yes
                  </label>
                  <label className="filter-checkbox" style={{
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e0e0e0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      checked={filters.blackBoxRequired === false}
                      onChange={() => setFilters(prev => ({ ...prev, blackBoxRequired: false }))}
                      style={{
                        width: '12px',
                        height: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    No
                  </label>
                  <label className="filter-checkbox" style={{
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e0e0e0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="radio"
                      checked={filters.blackBoxRequired === null}
                      onChange={() => setFilters(prev => ({ ...prev, blackBoxRequired: null }))}
                      style={{
                        width: '12px',
                        height: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    All
                  </label>
                </div>
              </div>

              <div className="filter-section" style={{ 
                marginBottom: '8px',
                paddingBottom: '8px'
              }}>
                <h4 style={{ 
                  fontSize: '12px',
                  marginBottom: '4px',
                  color: '#b8c7d9',
                  fontWeight: '500'
                }}>Imaging Modes</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {availableImagingModes.map(mode => (
                    <label key={mode} className="filter-checkbox" style={{
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#e0e0e0',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={filters.imagingModes.includes(mode)}
                        onChange={() => handleImagingModesChange(mode)}
                        style={{
                          width: '12px',
                          height: '12px',
                          cursor: 'pointer'
                        }}
                      />
                      {mode}
                    </label>
                  ))}
                </div>
              </div>

              {/* Reset All Filters Button */}
              <button
                onClick={() => setFilters({
                  maxRange: [],
                  blackBoxRequired: null,
                  imagingModes: []
                })}
                style={{
                  width: '100%',
                  padding: '6px',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  color: '#b8c7d9',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Reset All Filters
              </button>
            </div>
          </div>
        )}
        
        {/* Main Content Area */}
        <div style={{
          width: transducerType === 'liveImaging' ? '75%' : '100%',
          padding: '0 20px 20px 20px',
        }}>
          <h1 style={{ 
            color: '#ffffff',
            fontSize: '22px',
            marginBottom: '10px',
            marginTop: '0px'
          }}>{getTitle()}</h1>

          {filteredTransducers.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              color: '#b8c7d9',
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              margin: '20px 0'
            }}>
              <span style={{ fontSize: '24px', marginBottom: '12px' }}>🔍</span>
              <h3 style={{ 
                margin: '0 0 8px 0',
                color: '#ffffff',
                fontSize: '18px'
              }}>No Matching Transducers Found</h3>
              <p style={{ 
                margin: '0',
                fontSize: '14px',
                maxWidth: '400px',
                lineHeight: '1.5'
              }}>
                Try adjusting your filters to see more options.
              </p>
            </div>
          ) : (
            <GridContainer>
              {filteredTransducers.map(transducer => (
                <div key={transducer.id} style={{
                  backgroundColor: '#1a1a1a',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  width: '100%'
                }}>
                  <ImageContainer onClick={() => setModalImage({ isOpen: true, url: transducer.image, alt: transducer.name })} style={{ position: 'relative' }}>
                    <ProductImage 
                      src={transducer.image} 
                      alt={transducer.name}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="white" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </div>
                  </ImageContainer>

                  <div style={{ padding: '12px' }}>
                    <h3 style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#fff' }}>{transducer.name}</h3>
                    {/* Pricing display with best price support */}
                    {(() => {
                      const transducerPricing = lowestPrices[transducer.id];
                      const hasLowestPrice = transducerPricing && transducerPricing.price;
                      const formatPrice = (price) => {
                        if (typeof price === 'number') {
                          return price.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          });
                        }
                        return price;
                      };

                      if (hasLowestPrice) {
                        return (
                          <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                            <p style={{ color: '#4CAF50', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                              Best Price: ${formatPrice(transducerPricing.price)}
                            </p>
                            {transducerPricing.msrpSavings && (
                              <div style={{
                                marginTop: '4px',
                                fontSize: '12px',
                                color: '#e67e22',
                                fontWeight: '500'
                              }}>
                                ↓ Save ${formatPrice(transducerPricing.msrpSavings.amount)} off {transducerPricing.msrpSavings.reference || 'MSRP'}
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        return (
                          <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                            <p style={{ color: '#4CAF50', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                              MSRP: ${formatPrice(transducer.msrp)}
                              <Tooltip text={'💰 This may not be the best price! Select your components, then click Find Best Price on the bundle builder page.'} />
                            </p>
                            {transducer.csvLookupId && !transducerPricing && (
                              <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px' }}>
                                📡 Loading best price...
                              </div>
                            )}
                          </div>
                        );
                      }
                    })()}

                    {/* If it's a live imaging transducer, show expanded specs */}
                    {transducerType === 'liveImaging' && (
                      <div className="transducer-specs" style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '0',
                        fontSize: '0.9em',
                        marginBottom: '10px'
                      }}>
                        {/* Descriptive blurb about the transducer */}
                        {transducer.id === 'livescope-plus-lvs34' ? (
                          <div style={{ marginBottom: 10, fontSize: '0.82em' }}>
                            <div style={{ fontWeight: 700, fontSize: '1em', marginBottom: 2 }}>
                              Best All-Around | Most Popular | Top Clarity
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              The gold standard for real-time sonar. Incredible clarity and separation make it ideal for most fishing styles.
                            </div>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              <li style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                                <span style={{ color: '#22c55e', fontSize: '1.2em', marginRight: 6 }}>✅</span>
                                Great for seeing fish and structure clearly at typical freshwater depths.
                              </li>
                              <li style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ color: '#22c55e', fontSize: '1.2em', marginRight: 6 }}>✅</span>
                                Best balance of detail, performance, and versatility.
                              </li>
                            </ul>
                          </div>
                        ) : transducer.id === 'livescope-xr-lvs62' ? (
                          <div style={{ marginBottom: 10, fontSize: '0.82em' }}>
                            <div style={{ fontWeight: 700, fontSize: '1em', marginBottom: 2 }}>
                              Best for Deep/Open Water | Longest Range
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              Engineered for offshore and deep water use with extended range: over double the reach of LVS34.
                            </div>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              <li style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                                <span style={{ color: '#22c55e', fontSize: '1.2em', marginRight: 6 }}>✅</span>
                                Ideal for locating deep structure or distant schools of fish.
                              </li>
                              <li style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ color: '#22c55e', fontSize: '1.2em', marginRight: 6 }}>✅</span>
                                The go-to for open water and Great Lakes anglers.
                              </li>
                            </ul>
                          </div>
                        ) : transducer.id === 'panoptix-ps22' ? (
                          <div style={{ marginBottom: 10, fontSize: '0.82em' }}>
                            <div style={{ fontWeight: 700, fontSize: '1em', marginBottom: 2 }}>
                              Most Affordable | Simple Setup | No Black Box
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              Entry-level live sonar with forward and down view. One-knob angle adjustment and direct display connection.
                            </div>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              <li style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                                <span style={{ color: '#22c55e', fontSize: '1.2em', marginRight: 6 }}>✅</span>
                                Perfect for trolling motors and simplified installs.
                              </li>
                              <li style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ color: '#22c55e', fontSize: '1.2em', marginRight: 6 }}>✅</span>
                                Real-time views without the complexity or cost of LiveScope.
                              </li>
                            </ul>
                          </div>

                        ) : (
                          <div style={{ marginBottom: 10 }}>
                            <p style={{ margin: '0 0 4px 0' }}>{transducer.description}</p>
                          </div>
                        )}

                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 2px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Max Range{glossary['Max Range'] && <Tooltip text={glossary['Max Range']} />}</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.maxRange || '200 ft'}</span>
                        </div>
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 2px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Imaging Modes{glossary['Imaging Modes'] && <Tooltip text={glossary['Imaging Modes']} />}</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.imagingModes || 'Forward, Down'}</span>
                        </div>
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 2px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Typical Power Draw:</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.powerDraw12V} A</span>
                        </div>

                        {/* Expandable specs section */}
                        {expandedCards.has(transducer.id) ? (
                          <>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 2px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Target Separation{glossary['Target Separation'] && <Tooltip text={glossary['Target Separation']} />}</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.targetSeparation || 'Not specified'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 2px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Beam Angle{glossary['Beam Angle'] && <Tooltip text={glossary['Beam Angle']} />}</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.beamAngle || 'Not specified'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 2px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Black Box Required{glossary['Black Box Required'] && <Tooltip text={glossary['Black Box Required']} />}</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.blackBoxRequired ? 'Yes' : 'No'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 2px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Dimensions:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.dimensions || 'Not specified'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 2px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Release Year:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.releaseYear || 'Not specified'}</span>
                            </div>
                            
                            {/* Additional specs for certain models */}
                            {transducer.sonarFrequency && (
                              <div className="spec-row" style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 2px',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Frequency:</span>
                                <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.sonarFrequency}</span>
                              </div>
                            )}
                          </>
                        ) : (
                          // Show a faded version of the 4th feature when not expanded
                          <div className="spec-row" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '4px 2px',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            opacity: 0.6,
                            position: 'relative'
                          }}>
                            <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Target Separation{glossary['Target Separation'] && <Tooltip text={glossary['Target Separation']} />}</span>
                            <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.targetSeparation || 'Not specified'}</span>
                            {/* Gradient overlay to fade the content */}
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(to bottom, rgba(26, 26, 26, 0) 0%, rgba(26, 26, 26, 0.8) 100%)',
                              pointerEvents: 'none'
                            }} />
                          </div>
                        )}
                        
                        {/* Show/Hide button */}
                        <button
                          onClick={(e) => toggleCardExpansion(transducer.id, e)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#b8c7d9',
                            width: '100%',
                            padding: '4px 0',
                            marginTop: '4px',
                            fontSize: '0.9em',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {expandedCards.has(transducer.id) ? (
                            <>Show Less <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▲</span></>
                          ) : (
                            <>Show More Specs <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▼</span></>
                          )}
                        </button>
                      </div>
                    )}

                    {/* For open water and ice fishing transducers, show detailed specs too */}
                    {(transducerType === 'openWater' || transducerType === 'iceFishing') && (
                      <div className="transducer-specs" style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '0',
                        fontSize: '0.9em',
                        marginBottom: '10px'
                      }}>
                        {/* Descriptive section with best for badge and benefits */}
                        <div style={{ marginBottom: 10, fontSize: '0.82em' }}>
                          <div style={{ fontWeight: 700, fontSize: '1em', marginBottom: 2 }}>
                            {transducer.bestFor ? `Best for ${transducer.bestFor}` : 'Reliable Performance'}
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            {transducer.description}
                          </div>
                          {transducer.benefits && (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {transducer.benefits.map((benefit, index) => (
                                <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                                  <span style={{ color: '#22c55e', fontSize: '1.2em', marginRight: 6 }}>✅</span>
                                  {benefit}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {/* Specs rows for open water and ice fishing */}
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 2px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Max Depth</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.maxRange || 'Not specified'}</span>
                        </div>
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 2px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Frequency</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.sonarFrequency || 'Not specified'}</span>
                        </div>
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 2px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Target Separation</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.targetSeparation || 'Not specified'}</span>
                        </div>
                        
                        {/* Show more specs when expanded */}
                        {expandedCards.has(transducer.id) ? (
                          <>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 2px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Beam Angle</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.beamAngle || 'Not specified'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 2px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Power Draw</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.powerDraw12V ? `${transducer.powerDraw12V} A` : 'Not specified'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 2px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Installation</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.installationType || 'Standard'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 2px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Release Year</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.releaseYear || 'Not specified'}</span>
                            </div>
                          </>
                        ) : (
                          // Show a faded version of the 4th feature when not expanded
                          <div className="spec-row" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '4px 2px',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            opacity: 0.6,
                            position: 'relative'
                          }}>
                            <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Beam Angle</span>
                            <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{transducer.beamAngle || 'Not specified'}</span>
                            {/* Gradient overlay to fade the content */}
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(to bottom, rgba(26, 26, 26, 0) 0%, rgba(26, 26, 26, 0.8) 100%)',
                              pointerEvents: 'none'
                            }} />
                          </div>
                        )}
                        
                        {/* Show/Hide button */}
                        <button
                          onClick={(e) => toggleCardExpansion(transducer.id, e)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#b8c7d9',
                            width: '100%',
                            padding: '4px 0',
                            marginTop: '4px',
                            fontSize: '0.9em',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {expandedCards.has(transducer.id) ? (
                            <>Show Less <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▲</span></>
                          ) : (
                            <>Show More Specs <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▼</span></>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Compact Styled Action Buttons */}
                    <div style={{
                      marginTop: '8px',
                      padding: '6px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{
                        textAlign: 'center',
                        marginBottom: '6px',
                        color: '#ffffff',
                        fontSize: '0.6em',
                        fontWeight: '600',
                        opacity: 0.9,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        Choose Your Next Step:
                      </div>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {transducer.csvLookupId && (
                          <div style={{ textAlign: 'center', width: '100%' }}>
                            <FindBestPriceButton
                              productId={transducer.csvLookupId}
                              productType="transducer"
                              text={
                                <div style={{ 
                                  textAlign: 'center', 
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  gap: '2px'
                                }}>
                                  <div style={{ lineHeight: '1.1' }}>💰 Find Best Price</div>
                                  <div style={{
                                    fontSize: '0.85em',
                                    fontWeight: '400',
                                    opacity: 0.9,
                                    lineHeight: '1.1'
                                  }}>
                                    Compare prices across stores
                                  </div>
                                </div>
                              }
                              style={{
                                width: '100%',
                                fontSize: '0.7em',
                                padding: '6px 8px',
                                background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                                border: 'none',
                                color: 'white',
                                borderRadius: '4px',
                                fontWeight: '600',
                                textAlign: 'center',
                                display: 'flex !important',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                                margin: '0 !important',
                                maxWidth: '100%',
                                minWidth: '0',
                                flex: 'none'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          </div>
                        )}
                        
                        <div style={{
                          textAlign: 'center',
                          fontSize: '0.55em',
                          color: '#aaa',
                          margin: '0',
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          or
                        </div>
                        
                        <button 
                          onClick={() => handleSelect(transducer)}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            fontSize: '0.75em',
                            background: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
                            border: 'none',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '2px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #0077dd 0%, #0055aa 100%)';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #0066cc 0%, #004499 100%)';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          <div style={{ lineHeight: '1.1' }}>
                            🔧 Select & Build Setup
                          </div>
                          <div style={{
                            fontSize: '0.85em',
                            fontWeight: '400',
                            opacity: 0.9,
                            lineHeight: '1.1'
                          }}>
                            Add screen, battery, etc.
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </GridContainer>
          )}
        </div>
      </div>

      {modalImage.isOpen && (
        <ImageModal
          isOpen={modalImage.isOpen}
          imageUrl={modalImage.url}
          altText={modalImage.alt}
          onClose={() => setModalImage({ isOpen: false, url: '', alt: '' })}
        />
      )}
    </div>
  );
};

export default TransducerSelection;
